/**
 * Configuração de campos por categoria de produto
 * Define quais campos aparecem ao criar produtos, orçamentos e pedidos
 * baseado na categoria selecionada
 */

export interface CategoryFieldConfig {
  category: string;
  fields: SpecificationField[];
  description: string;
}

export interface SpecificationField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'textarea';
  placeholder?: string;
  required: boolean;
  options?: { value: string; label: string }[];
  unit?: string; // ex: "cm", "m", "%"
}

/**
 * Mapeamento de campos por categoria
 */
export const CATEGORY_FIELDS_CONFIG: Record<string, CategoryFieldConfig> = {
  cortinas: {
    category: 'Cortinas',
    description: 'Campos específicos para confecção de cortinas',
    fields: [
      {
        key: 'tipoCortinaId',
        label: 'Tipo de Cortina',
        type: 'select',
        placeholder: 'Selecione o tipo de cortina',
        required: false,
      },
      {
        key: 'tipoTecido',
        label: 'Tipo de Tecido',
        type: 'select',
        placeholder: 'Selecione o tipo de tecido',
        required: false,
        options: [
          { value: 'algodao', label: 'Algodão' },
          { value: 'poliester', label: 'Poliéster' },
          { value: 'linho', label: 'Linho' },
          { value: 'mistura', label: 'Mistura' },
          { value: 'veludo', label: 'Veludo' },
          { value: 'lace', label: 'Renda' },
          { value: 'blackout', label: 'Blackout' },
          { value: 'lam', label: 'Lamê' },
          { value: 'outro', label: 'Outro' },
        ],
      },
      {
        key: 'tipoAbertura',
        label: 'Tipo de Abertura',
        type: 'select',
        placeholder: 'Selecione o tipo de abertura',
        required: false,
        options: [
          { value: 'passante', label: 'Passante' },
          { value: 'meia_volta', label: 'Meia Volta' },
          { value: 'corrediça', label: 'Corrediça' },
          { value: 'guilhotina', label: 'Guilhotina' },
          { value: 'romana', label: 'Romana' },
          { value: 'painel', label: 'Painel Japonês' },
          { value: 'rolô', label: 'Rolô' },
          { value: 'outro', label: 'Outro' },
        ],
      },
      {
        key: 'condicaoDesconto',
        label: 'Condição de Desconto',
        type: 'text',
        placeholder: 'Ex: 10% em altura exata, 15% acima de 3 tubos',
        required: false,
      },
      {
        key: 'altura',
        label: 'Altura',
        type: 'number',
        placeholder: '200',
        unit: 'cm',
        required: false,
      },
      {
        key: 'largura',
        label: 'Largura',
        type: 'number',
        placeholder: '150',
        unit: 'cm',
        required: false,
      },
      {
        key: 'profundidade',
        label: 'Profundidade / Tubo',
        type: 'number',
        placeholder: '10',
        unit: 'cm',
        required: false,
      },
      {
        key: 'trilhoVarao',
        label: 'Trilho / Varão',
        type: 'text',
        placeholder: 'Ex: Trilho duplo, Varão madeira',
        required: false,
      },
      {
        key: 'acabamento',
        label: 'Acabamento',
        type: 'text',
        placeholder: 'Ex: Bainha simples, Com franja',
        required: false,
      },
      {
        key: 'observacoes',
        label: 'Observações Técnicas',
        type: 'textarea',
        placeholder: 'Qualquer detalhe importante...',
        required: false,
      },
    ],
  },

  bones: {
    category: 'Bonés',
    description: 'Campos específicos para confecção de bonés',
    fields: [
      {
        key: 'tamanho',
        label: 'Tamanho',
        type: 'select',
        placeholder: 'Selecione o tamanho',
        required: false,
        options: [
          { value: 'unico', label: 'Único' },
          { value: 'pp', label: 'PP' },
          { value: 'p', label: 'P' },
          { value: 'm', label: 'M' },
          { value: 'g', label: 'G' },
          { value: 'gg', label: 'GG' },
          { value: 'ajustavel', label: 'Ajustável' },
        ],
      },
      {
        key: 'tipoBordado',
        label: 'Tipo de Bordado',
        type: 'select',
        placeholder: 'Com bordado?',
        required: false,
        options: [
          { value: 'sim', label: 'Sim' },
          { value: 'nao', label: 'Não' },
        ],
      },
      {
        key: 'corBase',
        label: 'Cor Base',
        type: 'text',
        placeholder: 'Ex: Preto, Branco, Azul',
        required: false,
      },
      {
        key: 'design',
        label: 'Design / Arte',
        type: 'text',
        placeholder: 'Ex: Logo da empresa, padrão',
        required: false,
      },
      {
        key: 'quantidade',
        label: 'Quantidade',
        type: 'number',
        placeholder: '10',
        unit: 'unidades',
        required: false,
      },
      {
        key: 'observacoes',
        label: 'Observações',
        type: 'textarea',
        placeholder: 'Detalhes adicionais...',
        required: false,
      },
    ],
  },

  aviamentos: {
    category: 'Aviamentos',
    description: 'Campos específicos para aviamentos (botões, zíperes, etc)',
    fields: [
      {
        key: 'tipo',
        label: 'Tipo',
        type: 'select',
        placeholder: 'Selecione o tipo',
        required: false,
        options: [
          { value: 'botao', label: 'Botão' },
          { value: 'ziper', label: 'Zíper' },
          { value: 'elastico', label: 'Elástico' },
          { value: 'fita', label: 'Fita' },
          { value: 'ganchos', label: 'Ganchos' },
          { value: 'ilhos', label: 'Ilhós' },
          { value: 'velcro', label: 'Velcro' },
        ],
      },
      {
        key: 'cor',
        label: 'Cor',
        type: 'text',
        placeholder: 'Ex: Preto, Branco, Azul',
        required: false,
      },
      {
        key: 'tamanho',
        label: 'Tamanho / Medida',
        type: 'text',
        placeholder: 'Ex: 5cm, 10mm, P/M/G',
        required: false,
      },
      {
        key: 'quantidade',
        label: 'Quantidade',
        type: 'number',
        placeholder: '100',
        unit: 'unidades',
        required: false,
      },
      {
        key: 'material',
        label: 'Material',
        type: 'text',
        placeholder: 'Ex: Plástico, Metal, Resina',
        required: false,
      },
      {
        key: 'observacoes',
        label: 'Observações',
        type: 'textarea',
        placeholder: 'Detalhes técnicos...',
        required: false,
      },
    ],
  },

  bordado: {
    category: 'Bordado',
    description: 'Campos específicos para serviço de bordado',
    fields: [
      {
        key: 'tipoBordado',
        label: 'Tipo de Bordado',
        type: 'select',
        placeholder: 'Selecione o tipo',
        required: false,
        options: [
          { value: 'maquina', label: 'Máquina' },
          { value: 'manual', label: 'Manual' },
          { value: 'mista', label: 'Mista' },
        ],
      },
      {
        key: 'larguraBordado',
        label: 'Largura do Bordado',
        type: 'number',
        placeholder: '10',
        unit: 'cm',
        required: false,
      },
      {
        key: 'alturaBordado',
        label: 'Altura do Bordado',
        type: 'number',
        placeholder: '10',
        unit: 'cm',
        required: false,
      },
      {
        key: 'posicao',
        label: 'Posição',
        type: 'select',
        placeholder: 'Selecione a posição',
        required: false,
        options: [
          { value: 'peito', label: 'Peito' },
          { value: 'costa', label: 'Costa' },
          { value: 'lateral', label: 'Lateral' },
          { value: 'manga', label: 'Manga' },
          { value: 'costas_inteira', label: 'Costas Inteira' },
        ],
      },
      {
        key: 'cores',
        label: 'Cores Utilizadas',
        type: 'text',
        placeholder: 'Ex: Vermelho, Azul, Branco',
        required: false,
      },
      {
        key: 'quantidade',
        label: 'Quantidade de Peças',
        type: 'number',
        placeholder: '10',
        unit: 'peças',
        required: false,
      },
      {
        key: 'observacoes',
        label: 'Observações',
        type: 'textarea',
        placeholder: 'Detalhes do design...',
        required: false,
      },
    ],
  },

  botoes: {
    category: 'Botões',
    description: 'Campos específicos para botões',
    fields: [
      {
        key: 'material',
        label: 'Material',
        type: 'select',
        placeholder: 'Selecione o material',
        required: false,
        options: [
          { value: 'plastico', label: 'Plástico' },
          { value: 'metal', label: 'Metal' },
          { value: 'madeira', label: 'Madeira' },
          { value: 'vidro', label: 'Vidro' },
          { value: 'resina', label: 'Resina' },
          { value: 'couro', label: 'Couro' },
        ],
      },
      {
        key: 'tamanho',
        label: 'Tamanho',
        type: 'text',
        placeholder: 'Ex: 20mm, 1 polegada',
        required: false,
      },
      {
        key: 'cor',
        label: 'Cor',
        type: 'text',
        placeholder: 'Ex: Preto, Dourado, Prata',
        required: false,
      },
      {
        key: 'quantidade',
        label: 'Quantidade',
        type: 'number',
        placeholder: '100',
        unit: 'unidades',
        required: false,
      },
      {
        key: 'tipo',
        label: 'Tipo de Botão',
        type: 'select',
        placeholder: 'Selecione o tipo',
        required: false,
        options: [
          { value: 'costura', label: 'De Costura' },
          { value: 'snap', label: 'Snap' },
          { value: 'ilhos', label: 'Ilhós' },
          { value: 'pressao', label: 'De Pressão' },
        ],
      },
      {
        key: 'observacoes',
        label: 'Observações',
        type: 'textarea',
        placeholder: 'Especificações técnicas...',
        required: false,
      },
    ],
  },

  estampado: {
    category: 'Estampado',
    description: 'Campos específicos para serviço de estamparia',
    fields: [
      {
        key: 'tipoEstampa',
        label: 'Tipo de Estampa',
        type: 'select',
        placeholder: 'Selecione o tipo',
        required: false,
        options: [
          { value: 'silk_screen', label: 'Silk Screen' },
          { value: 'sublimacao', label: 'Sublimação' },
          { value: 'transfer', label: 'Transfer' },
          { value: 'dtg', label: 'DTG (Impressão Direta)' },
          { value: 'bordado', label: 'Bordado' },
        ],
      },
      {
        key: 'larguraEstampa',
        label: 'Largura da Estampa',
        type: 'number',
        placeholder: '20',
        unit: 'cm',
        required: false,
      },
      {
        key: 'alturaEstampa',
        label: 'Altura da Estampa',
        type: 'number',
        placeholder: '20',
        unit: 'cm',
        required: false,
      },
      {
        key: 'posicao',
        label: 'Posição',
        type: 'select',
        placeholder: 'Selecione a posição',
        required: false,
        options: [
          { value: 'frente', label: 'Frente' },
          { value: 'costa', label: 'Costa' },
          { value: 'lateral', label: 'Lateral' },
          { value: 'manga', label: 'Manga' },
        ],
      },
      {
        key: 'cores',
        label: 'Número de Cores',
        type: 'number',
        placeholder: '4',
        unit: 'cores',
        required: false,
      },
      {
        key: 'tipoTinta',
        label: 'Tipo de Tinta',
        type: 'text',
        placeholder: 'Ex: Plastisol, Agua, Poliuretano',
        required: false,
      },
      {
        key: 'quantidade',
        label: 'Quantidade de Peças',
        type: 'number',
        placeholder: '50',
        unit: 'peças',
        required: false,
      },
      {
        key: 'observacoes',
        label: 'Observações',
        type: 'textarea',
        placeholder: 'Detalhes da estampa...',
        required: false,
      },
    ],
  },

  linhas: {
    category: 'Linhas',
    description: 'Campos específicos para linhas de costura',
    fields: [
      {
        key: 'cor',
        label: 'Cor',
        type: 'text',
        placeholder: 'Ex: Branco, Preto, Colorido',
        required: false,
      },
      {
        key: 'tipo',
        label: 'Tipo de Linha',
        type: 'select',
        placeholder: 'Selecione o tipo',
        required: false,
        options: [
          { value: 'poliester', label: 'Poliéster' },
          { value: 'algodao', label: 'Algodão' },
          { value: 'nylon', label: 'Nylon' },
          { value: 'monofilamento', label: 'Monofilamento' },
        ],
      },
      {
        key: 'numero',
        label: 'Número / Espessura',
        type: 'text',
        placeholder: 'Ex: 40, 60, 120',
        required: false,
      },
      {
        key: 'tamanhoCarretel',
        label: 'Tamanho do Carretel',
        type: 'number',
        placeholder: '1000',
        unit: 'metros',
        required: false,
      },
      {
        key: 'quantidade',
        label: 'Quantidade de Carretéis',
        type: 'number',
        placeholder: '10',
        unit: 'carretéis',
        required: false,
      },
      {
        key: 'observacoes',
        label: 'Observações',
        type: 'textarea',
        placeholder: 'Especificações adicionais...',
        required: false,
      },
    ],
  },

  tecidos: {
    category: 'Tecidos',
    description: 'Campos específicos para venda/fornecimento de tecidos',
    fields: [
      {
        key: 'composicao',
        label: 'Composição',
        type: 'text',
        placeholder: 'Ex: 100% Algodão, 80% Poliéster 20% Algodão',
        required: false,
      },
      {
        key: 'cor',
        label: 'Cor',
        type: 'text',
        placeholder: 'Ex: Azul Royal, Preto, Mescla',
        required: false,
      },
      {
        key: 'larguraTecido',
        label: 'Largura do Tecido',
        type: 'number',
        placeholder: '150',
        unit: 'cm',
        required: false,
      },
      {
        key: 'metragem',
        label: 'Metragem Total',
        type: 'number',
        placeholder: '50',
        unit: 'metros',
        required: false,
      },
      {
        key: 'tipoTecido',
        label: 'Tipo de Tecido',
        type: 'select',
        placeholder: 'Selecione o tipo',
        required: false,
        options: [
          { value: 'basico', label: 'Básico' },
          { value: 'estampado', label: 'Estampado' },
          { value: 'jacquard', label: 'Jacquard' },
          { value: 'linho', label: 'Linho' },
          { value: 'seda', label: 'Seda' },
          { value: 'lã', label: 'Lã' },
        ],
      },
      {
        key: 'gramaturaEstimada',
        label: 'Gramatura Estimada',
        type: 'text',
        placeholder: 'Ex: 150g/m²',
        required: false,
      },
      {
        key: 'observacoes',
        label: 'Observações',
        type: 'textarea',
        placeholder: 'Características do tecido...',
        required: false,
      },
    ],
  },

  personalizado: {
    category: 'Personalizado',
    description: 'Campos genéricos para produtos personalizados',
    fields: [
      {
        key: 'descricaoProduto',
        label: 'Descrição do Produto',
        type: 'textarea',
        placeholder: 'Descreva o produto em detalhes...',
        required: false,
      },
      {
        key: 'medidas',
        label: 'Medidas Principais',
        type: 'text',
        placeholder: 'Ex: 10cm x 20cm x 5cm',
        required: false,
      },
      {
        key: 'materiais',
        label: 'Materiais Utilizados',
        type: 'text',
        placeholder: 'Ex: Algodão, Poliéster, Metal',
        required: false,
      },
      {
        key: 'personalizacoes',
        label: 'Personalizações',
        type: 'text',
        placeholder: 'Ex: Logo, Nome, Design Especial',
        required: false,
      },
      {
        key: 'quantidade',
        label: 'Quantidade',
        type: 'number',
        placeholder: '10',
        unit: 'unidades',
        required: false,
      },
      {
        key: 'observacoes',
        label: 'Observações Técnicas',
        type: 'textarea',
        placeholder: 'Detalhes especiais...',
        required: false,
      },
    ],
  },

  roupas: {
    category: 'Roupas',
    description: 'Campos padrão do Ateliê Pro para roupas',
    fields: [
      {
        key: 'medidas',
        label: 'Usar sistema de medidas existente',
        type: 'text',
        required: false,
      },
    ],
  },

  outras: {
    category: 'Outras',
    description: 'Campos padrão do Ateliê Pro',
    fields: [
      {
        key: 'nenhum',
        label: 'Sem campos especiais',
        type: 'text',
        required: false,
      },
    ],
  },
};

/**
 * Obter configuração de campos para uma categoria
 */
export function getCategoryFieldConfig(categoryName: string): CategoryFieldConfig | null {
  const key = categoryName.toLowerCase();

  // Verifica cada categoria
  if (key.includes('cortina')) return CATEGORY_FIELDS_CONFIG.cortinas;
  if (key.includes('boné') || key.includes('bone')) return CATEGORY_FIELDS_CONFIG.bones;
  if (key.includes('aviamento')) return CATEGORY_FIELDS_CONFIG.aviamentos;
  if (key.includes('bordado')) return CATEGORY_FIELDS_CONFIG.bordado;
  if (key.includes('botão') || key.includes('botao')) return CATEGORY_FIELDS_CONFIG.botoes;
  if (key.includes('estampado') || key.includes('estampa')) return CATEGORY_FIELDS_CONFIG.estampado;
  if (key.includes('linha')) return CATEGORY_FIELDS_CONFIG.linhas;
  if (key.includes('tecido')) return CATEGORY_FIELDS_CONFIG.tecidos;
  if (key.includes('personalizado')) return CATEGORY_FIELDS_CONFIG.personalizado;
  if (key.includes('roupa')) return CATEGORY_FIELDS_CONFIG.roupas;

  return null;
}

/**
 * Verificar se uma categoria tem campos especiais
 */
export function hasSpecialFields(categoryName: string): boolean {
  return getCategoryFieldConfig(categoryName) !== null;
}

/**
 * Verificar se é cortina
 */
export function isCurtainCategory(categoryName: string): boolean {
  return categoryName.toLowerCase().includes('cortina');
}

/**
 * Verificar se é roupa
 */
export function isClothingCategory(categoryName: string): boolean {
  return categoryName.toLowerCase().includes('roupa') ||
         categoryName.toLowerCase().includes('personalizado');
}
